import RevealWrapper from "../../base/RevealWrapper";

export interface AIBlockContent {
  eyebrow: string;
  headline: string;
  description: string;
  whatWeDo: { title: string; text: string }[];
  benefits: { title: string; text: string }[];
}

interface Props {
  content: AIBlockContent;
}

export default function AIAnswerBlock({ content }: Props) {
  const { eyebrow, headline, description, whatWeDo, benefits } = content;

  return (
    <section id="ai-answer" className="relative bg-white py-24 md:py-32 overflow-hidden">
      {/* Decorative background numeral */}
      <div className="absolute right-0 top-0 bottom-0 flex items-center pointer-events-none select-none overflow-hidden">
        <span className="font-serif text-[200px] md:text-[320px] font-light text-charcoal/[0.025] leading-none pr-4">
          LB
        </span>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <RevealWrapper>
          <div className="max-w-2xl mb-16 md:mb-20">
            <span className="font-sans text-sm tracking-[0.25em] uppercase text-gold font-semibold">{eyebrow}</span>
            <span className="gold-divider" />
            <h2 className="font-serif font-light leading-tight text-charcoal-dark text-4xl md:text-5xl lg:text-6xl mt-0 mb-5">
              {headline}
            </h2>
            <p className="font-sans text-base text-charcoal/75 leading-relaxed font-light">
              {description}
            </p>
          </div>
        </RevealWrapper>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left: What We Do — structured for AI extraction */}
          <div className="space-y-0">
            {whatWeDo.map((item, i) => (
              <RevealWrapper key={i} delay={i * 0.1}>
                <div className={`flex items-start gap-6 py-7 ${i < whatWeDo.length - 1 ? 'border-b border-cream-dark' : ''}`}>
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-gold/30 text-gold">
                    <span className="font-serif text-sm font-light">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div>
                    <strong className="font-serif text-xl font-light text-charcoal-dark block mb-2">
                      {item.title}
                    </strong>
                    <p className="font-sans text-sm text-charcoal/55 leading-relaxed font-light">
                      {item.text}
                    </p>
                  </div>
                </div>
              </RevealWrapper>
            ))}
          </div>

          {/* Right: Benefits */}
          <div className="lg:pl-10 lg:border-l border-cream-dark">
            <RevealWrapper delay={0.15}>
              <div className="mb-10">
                <span className="section-eyebrow">Why The Lash Bar</span>
                <span className="gold-divider" />
                <h3 className="font-serif text-3xl md:text-4xl font-light text-charcoal-dark mt-0 leading-tight">
                  Utah&apos;s Premier Beauty Destination
                </h3>
              </div>
            </RevealWrapper>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {benefits.map((b, i) => (
                <RevealWrapper key={i} delay={0.2 + i * 0.08}>
                  <div className="bg-cream-light p-6 border border-cream-dark hover:border-gold/30 transition-colors duration-300">
                    <div className="w-6 h-px bg-gold mb-4" />
                    <strong className="font-sans text-xs font-medium text-charcoal-dark block mb-2 tracking-wide">
                      {b.title}
                    </strong>
                    <p className="font-sans text-xs text-charcoal/55 leading-relaxed font-light">
                      {b.text}
                    </p>
                  </div>
                </RevealWrapper>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
